import { ArrowRight } from 'lucide-react';
import { ContractAddress } from './ContractAddress.js';

const PILLARS = [
  { number: '01', label: 'Conversational' },
  { number: '02', label: 'Connected' },
  { number: '03', label: 'Compliant' },
];

/**
 * The one place on the site where type sits directly on the footage rather than
 * on a plate. On the light theme that is the shipped composition and it is left
 * alone: dark type, light film. Inverting the type for the dark theme removes
 * that safety, so the dark theme gets a ground of its own — a soft ellipse of
 * `bg` behind the headline, opaque enough at the centre that the worst case
 * still reads. Against a blown-out white frame the composite under the type is
 * 0.85 × #0D0D0D over #FFFFFF = #313131, and #F2F2F2 on #313131 is 11.0:1.
 * It fades to nothing before the plate below, so the film is never boxed in.
 */
const HERO_SCRIM =
  'radial-gradient(130% 92% at 50% 26%, rgb(var(--bg) / 0.85) 0%, rgb(var(--bg) / 0.85) 32%, ' +
  'rgb(var(--bg) / 0.5) 58%, rgb(var(--bg) / 0) 86%)';

export function Hero() {
  return (
    <section className="relative flex flex-col items-center overflow-hidden h-screen">
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-x-0 top-0 h-[80%] hidden dark:block"
        style={{ background: HERO_SCRIM }}
      />

      <div className="relative z-10 flex flex-col items-center text-center pt-24 sm:pt-26 md:pt-32 px-4 sm:px-6">
        <h1 className="font-serif text-4xl sm:text-5xl md:text-7xl lg:text-8xl leading-[1.1] tracking-tighter text-ink font-normal">
          Build lasting
          <br />
          relationships.
        </h1>

        <p className="max-w-sm sm:max-w-md mt-5 sm:mt-6 md:mt-8 text-sm md:text-base text-ink/70 leading-relaxed">
          Agentic outreach for the Meta ecosystem — an envoy that finds the right companies, scores
          who will actually reply, and carries the conversation to a qualified handover.
        </p>

        <a
          href="/app"
          className="mt-6 sm:mt-8 md:mt-10 px-6 sm:px-8 py-3 sm:py-3.5 bg-accent text-white text-sm font-medium rounded-lg hover:bg-accent-hover transition-colors duration-200"
        >
          Dashboard
        </a>

        <ContractAddress />
      </div>

      <div className="relative z-10 mt-auto w-full max-w-5xl px-4 sm:px-6">
        <div className="bg-paper/90 dark:bg-paper/85 backdrop-blur-sm border border-hairline border-b-0 pt-8 sm:pt-12 md:pt-16 px-5 sm:px-8 md:px-12 pb-0 shadow-sm">
          <div className="grid md:grid-cols-2 gap-6 sm:gap-8 md:gap-16">
            <div>
              <div className="text-[11px] uppercase tracking-[0.2em] text-ink/50 font-medium">
                What do we do?
              </div>
              <h2 className="mt-3 text-2xl sm:text-3xl md:text-4xl font-serif font-normal leading-tight tracking-tight text-ink">
                Conversations that{' '}
                <br className="hidden sm:block" />
                build momentum
              </h2>
            </div>
            <div className="flex md:items-end">
              <p className="text-sm md:text-[15px] text-ink/70 leading-relaxed">
                Three layers on one data loop: a prospect graph built from public signals, scoring
                that predicts lift rather than luck, and an agent that holds a real conversation and
                shows its work. Built on the official Meta APIs.
              </p>
            </div>
          </div>

          <div className="mt-6 sm:mt-8 md:mt-10 h-px bg-hairline w-full" />

          <div className="grid sm:grid-cols-3 gap-2 sm:gap-3 mt-6 sm:mt-8 md:mt-10">
            {PILLARS.map((pillar) => (
              <a
                key={pillar.number}
                href="#product"
                className="group bg-panel hover:bg-panel-hover transition-all duration-200 cursor-pointer px-4 sm:px-6 py-3.5 sm:py-4 flex items-center justify-between"
              >
                <span className="text-sm text-ink">
                  <span className="text-ink/40">{pillar.number}</span>
                  <span className="mx-2 text-ink/30">/</span>
                  <span className="font-medium">{pillar.label}</span>
                </span>
                <ArrowRight className="w-4 h-4 text-ink/40 group-hover:text-ink/70 group-hover:translate-x-0.5 transition-all duration-200" />
              </a>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

export default Hero;
